/** biome-ignore-all lint/suspicious/noExplicitAny: false positive */
import { create } from "zustand";

export interface FlowTargetCredentialSchema {
	type: string;
	required?: string[];
	properties?: Record<string, any>;
}

export interface FlowTargetInputSchema {
	DEPOSIT?: Record<string, any>;
	WITHDRAW?: Record<string, any>;
}

export interface FlowTarget {
	id: string;
	name: string;
	logo: string;
	status: string;
	credentialSchema: FlowTargetCredentialSchema;
	inputSchema: FlowTargetInputSchema;
	flowTypeId: string;
	brandId: string | null;
	createdAt: string;
	updatedAt: string;
	createdBy: string;
	updatedBy: string;
}

export interface IFlowTarget {
	id: string;
	name: string;
	logo: string;
	status: string;
	flowTypeId?: string;
	trustScore?: number;
	actions: IFlowAction[];
}

export interface IFlowAction {
	id: string;
	name: string;
	definition: IFlowDefinition | null;
}

export interface IFlowDefinition {
	id: string;
	description: string;
	inputSchema: Record<string, unknown>;
	flow: IFlow | null;
}

export interface IFlow {
	id: string;
	status: string;
}

export interface IFlowListData {
	name: string;
	targets: IFlowTarget[];
}

export interface FlowType {
	id: string;
	name: string;
}

export interface CustomEditFlowPayload {
	flowTargetId: string;
	flowId?: string;
	flowStatus?: string;
	flowDefinitionId: string;
	flowDefinitionDescription: string;
	targetName: string;
	logo: string;
	actionName: string;
}

interface FlowState {
	flows: IFlowListData[];
	flowTypes: FlowType[];
	flowTargets: FlowTarget[];
	customEditFlowPayload: CustomEditFlowPayload[];
	selectedFlowType: FlowType | null;
	selectedFlowTypeName: string;
	selectedFlowTypeId: string;
	selectedProvider: FlowTarget | null;
	selectedEnvironmentSecret: string | null;
	isPanelOpen: boolean;
	isLoading: boolean;
	error: Error | null;

	setFlows: (flows: IFlowListData[]) => void;
	setFlowTypes: (flowTypes: FlowType[]) => void;
	setFlowTargets: (flowTargets: FlowTarget[]) => void;
	setCustomEditFlowPayload: (payload: CustomEditFlowPayload[]) => void;
	setSelectedFlowType: (flowType: FlowType) => void;
	setSelectedFlowTypeName: (name: string) => void;
	setSelectedFlowTypeId: (id: string) => void;
	setSelectedProvider: (provider: FlowTarget | null) => void;
	setSelectedEnvironmentSecret: (secret: string | null) => void;
	setIsPanelOpen: (isOpen: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
	setError: (error: Error | null) => void;

	openProviderDetails: (provider: FlowTarget) => void;
	closeSidePanel: () => void;
}

export const useFlowStore = create<FlowState>((set) => ({
	flows: [],
	flowTypes: [],
	flowTargets: [],
	customEditFlowPayload: [],
	selectedFlowType: null,
	selectedFlowTypeName: "",
	selectedFlowTypeId: "",
	selectedProvider: null,
	isPanelOpen: false,
	isLoading: false,
	error: null,
	selectedEnvironmentSecret: null,

	setFlows: (flows) => set({ flows }),
	setFlowTypes: (flowTypes) => set({ flowTypes }),
	setFlowTargets: (flowTargets) => set({ flowTargets }),
	setCustomEditFlowPayload: (payload) =>
		set({ customEditFlowPayload: payload }),
	setSelectedFlowType: (flowType) =>
		set({
			selectedFlowType: flowType,
			selectedFlowTypeName: flowType.name,
			selectedFlowTypeId: flowType.id
		}),
	setSelectedFlowTypeName: (name) => set({ selectedFlowTypeName: name }),
	setSelectedFlowTypeId: (id) => set({ selectedFlowTypeId: id }),
	setSelectedProvider: (provider) => set({ selectedProvider: provider }),
	setSelectedEnvironmentSecret: (secret) =>
		set({ selectedEnvironmentSecret: secret }),
	setIsPanelOpen: (isOpen) => set({ isPanelOpen: isOpen }),
	setIsLoading: (isLoading) => set({ isLoading }),
	setError: (error) => set({ error }),

	openProviderDetails: (provider) => {
		set({ selectedProvider: provider, isPanelOpen: true });
	},
	closeSidePanel: () => {
		set({ isPanelOpen: false });
	}
}));
